import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { PagedList } from "@/components/ListPagination";
import { BranchCell } from "@/components/BranchFlag";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Printer, FileDown, Search, Pencil, Trash2, Upload, FileCheck2, MessageCircle, ShieldCheck } from "lucide-react";
import { HEAD_OFFICE } from "@/lib/company";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDate, today, inr } from "@/lib/format";
import { printDeliveryReport } from "@/lib/print";
import { toast } from "sonner";

const monthStart = () => `${today().slice(0, 7)}-01`;
const cnOf = (r) => String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "");
const dDate = (r) => (r.delivery_date || String(r.delivered_at || "").slice(0, 10) || "");

function UiChaseButton({ pending, ageDays, custCode }) {
  const red = pending.filter((r) => (ageDays(r) ?? 0) > 15);
  const chase = () => {
    if (!red.length) {
      toast.info("No pending LRs older than 15 days. 🎉");
      return;
    }
    const msg = [
      "*NTC — Ageing Pending Deliveries (15+ days)*",
      "Oldest first:",
      ...red.slice(0, 20).map((r, i) => `${i + 1}. LR ${cnOf(r) || "—"} — ${r.customer_name || "—"}${custCode(r) ? ` (${custCode(r)})` : ""} — ${r.origin || "?"}→${r.destination || "?"} — *waiting ${ageDays(r)} days*`),
      ...(red.length > 20 ? [`…and ${red.length - 20} more`] : []),
      "",
      `Total: ${red.length} LR(s) pending beyond 15 days. Please follow up for delivery & POD.`,
      "",
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <Button
      size="sm"
      variant="outline"
      className={`h-7 text-xs ${red.length ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100" : "text-slate-500"}`}
      onClick={chase}
      data-testid="ageing-chase-btn"
    >
      <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Chase Oldest{red.length ? ` (${red.length})` : ""}
    </Button>
  );
}

export default function DeliveryReport() {
  const [rows, setRows] = useState(null);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [staff, setStaff] = useState("All");
  const [branch, setBranch] = useState("All");
  const [selected, setSelected] = useState(new Set());
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ delivery_date: "", vehicle_no: "", vehicle_type: "" });
  const [markRow, setMarkRow] = useState(null);
  const [otpRow, setOtpRow] = useState(null);
  const [otpMobile, setOtpMobile] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [markDate, setMarkDate] = useState(today());
  const [q, setQ] = useState("");
  const [customersList, setCustomersList] = useState([]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomersList(r.data || [])).catch(() => {});
  }, []);
  const codeByName = useMemo(() => {
    const m = {};
    customersList.forEach((c) => { if (c.name && c.customer_code) m[String(c.name).trim().toLowerCase()] = c.customer_code; });
    return m;
  }, [customersList]);
  const custCode = (r) => codeByName[String(r.customer_name || r.consignor || "").trim().toLowerCase()] || "";
  const ageDays = (r) => {
    const d = r.lr_date || r.dispatch_date || String(r.created_at || "").slice(0, 10);
    if (!d) return null;
    const n = Math.floor((new Date(today()) - new Date(d)) / 86400000);
    return Number.isFinite(n) ? Math.max(0, n) : null;
  };
  const ageCls = (n) =>
    n > 30
      ? "bg-red-900 text-white"
      : n > 15
      ? "bg-red-600 text-white"
      : n > 7
      ? "bg-orange-500 text-white"
      : n > 3
      ? "bg-amber-400 text-amber-950"
      : "bg-emerald-100 text-emerald-700";
  const fileInputs = useRef({});
  const [uploadingId, setUploadingId] = useState("");

  const onPodFile = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/consignments/${id}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("POD uploaded — consignment marked Delivered");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingId("");
      if (fileInputs.current[id]) fileInputs.current[id].value = "";
    }
  };

  const podCell = (r) => (
    <span className="inline-flex items-center gap-1.5">
      <input type="file" accept="image/*,.pdf" className="hidden" ref={(el) => (fileInputs.current[r.id] = el)} onChange={(e) => onPodFile(r.id, e)} data-testid="delivery-pod-file-input" />
      {r.pod_path ? (
        <a href={`${API}/files/${r.pod_path}?auth=${localStorage.getItem("ntc_token")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 underline underline-offset-2" data-testid="delivery-pod-view-link">
          <FileCheck2 className="h-3.5 w-3.5" /> POD ✓
        </a>
      ) : (
        <Button variant="outline" size="sm" className="h-7 text-xs" disabled={uploadingId === r.id} onClick={() => fileInputs.current[r.id]?.click()} data-testid="delivery-pod-upload-btn">
          <Upload className="mr-1 h-3 w-3" /> {uploadingId === r.id ? "Uploading…" : "Upload"}
        </Button>
      )}
    </span>
  );

  const whatsappDelivered = (r) => {
    const lines = [
      `✅ *DELIVERED — Nisha Transport Corporation*`,
      ``,
      `LR No: *${cnOf(r) || "—"}*`,
      `Route: ${r.origin || "—"} → ${r.destination || "—"}`,
      `Consignee: ${r.consignee || "—"}`,
      r.vehicle_no ? `Vehicle: ${r.vehicle_no}${r.vehicle_type ? ` (${r.vehicle_type})` : ""}` : "",
      `Delivered On: ${dDate(r) ? fmtDate(dDate(r)) : "—"}`,
      r.pod_path ? `POD: Uploaded ✓ (copy available on request)` : `POD: Will be shared shortly`,
      ``,
      `Thank you for your business!`,
    ].filter((l) => l !== "");
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  const load = () => {
    api.get("/shipments").then((r) => setRows(r.data || [])).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (r) => {
    setEditRow(r);
    setEditForm({ delivery_date: dDate(r), vehicle_no: r.vehicle_no || "", vehicle_type: r.vehicle_type || "" });
  };

  const saveEdit = async () => {
    try {
      await api.post(`/shipments/${editRow.id}/delivery`, { delivery_date: editForm.delivery_date, vehicle_no: editForm.vehicle_no, vehicle_type: editForm.vehicle_type });
      setRows((prev) => prev.map((x) => (x.id === editRow.id ? { ...x, delivery_date: editForm.delivery_date, vehicle_no: editForm.vehicle_no, vehicle_type: editForm.vehicle_type } : x)));
      setEditRow(null);
      toast.success("Delivery updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`Delete LR ${cnOf(r) || ""}? This cannot be undone.`)) return;
    try {
      await api.delete(`/shipments/${r.id}`);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      toast.success(`LR ${cnOf(r)} deleted`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const delivered = useMemo(
    () => (rows || []).filter((r) => (r.delivered_by || /delivered/i.test(r.status || "")) && dDate(r)),
    [rows]
  );
  const pending = useMemo(
    () =>
      (rows || []).filter((r) => {
        if ((r.delivered_by || /delivered/i.test(r.status || "")) && dDate(r)) return false;
        if (/cancelled/i.test(r.status || "")) return false;
        if (branch !== "All" && (r.office || r.office_name || "") !== branch) return false;
        const needle = q.trim().toLowerCase();
        if (needle) {
          const hay = [r.customer_name, custCode(r), cnOf(r), r.origin, r.destination, r.consignee, r.vehicle_no];
          if (!hay.some((x) => String(x || "").toLowerCase().includes(needle))) return false;
        }
        return true;
      })
      .sort((a, b) => (ageDays(b) ?? -1) - (ageDays(a) ?? -1)),
    [rows, branch, q, codeByName] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const markDelivered = async () => {
    try {
      const res = await api.post(`/shipments/${markRow.id}/mark-delivered`, { delivery_date: markDate });
      setRows((prev) => prev.map((x) => (x.id === markRow.id ? { ...x, delivery_date: res.data.delivery_date, status: "Delivered" } : x)));
      setMarkRow(null);
      toast.success(`LR ${cnOf(markRow)} marked delivered`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const staffNames = useMemo(
    () => Array.from(new Set(delivered.map((r) => r.delivered_by || "Unassigned"))).sort(),
    [delivered]
  );
  const branchNames = useMemo(
    () => Array.from(new Set(delivered.map((r) => r.office || r.office_name).filter(Boolean))).sort(),
    [delivered]
  );
  const filtered = useMemo(
    () =>
      delivered.filter((r) => {
        const d = dDate(r);
        if (from && d < from) return false;
        if (to && d > to) return false;
        if (staff !== "All" && (r.delivered_by || "Unassigned") !== staff) return false;
        if (branch !== "All" && (r.office || r.office_name || "") !== branch) return false;
        const needle = q.trim().toLowerCase();
        if (needle) {
          const hay = [r.customer_name, custCode(r), cnOf(r), r.delivered_by, r.origin, r.destination, r.consignee, r.vehicle_no];
          if (!hay.some((x) => String(x || "").toLowerCase().includes(needle))) return false;
        }
        return true;
      }),
    [delivered, from, to, staff, branch, q, codeByName] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const chosen = useMemo(
    () => (selected.size ? filtered.filter((r) => selected.has(r.id)) : filtered),
    [filtered, selected]
  );
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (s.size === filtered.length ? new Set() : new Set(filtered.map((r) => r.id))));

  const groups = useMemo(() => {
    const m = {};
    chosen.forEach((r) => { (m[r.delivered_by || "Unassigned"] = m[r.delivered_by || "Unassigned"] || []).push(r); });
    return Object.entries(m)
      .map(([name, list]) => [name, list.sort((a, b) => dDate(a).localeCompare(dDate(b)))])
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [chosen]);

  const exportCsv = () => {
    const headers = ["Sl No.", "Delivered By", "Branch", "LR No.", "LR Date", "From", "Destination", "Consignor Name", "Customer Code", "Consignee Name", "Basis", "PKG", "Invoice No.", "Invoice Date", "Invoice Value", "Vehicle No", "Vehicle Type", "Freight Amount", "Delivered On"];
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [headers.join(",")];
    let i = 0;
    groups.forEach(([name, list]) => {
      list.forEach((r) => {
        i += 1;
        lines.push([i, name, r.office || "Chennai", cnOf(r), r.lr_date || r.dispatch_date ? fmtDate(r.lr_date || r.dispatch_date) : "", r.origin || "", r.destination || "", r.customer_name || "", custCode(r) || "", r.consignee || "", r.basis || "", r.packages || "", r.invoice_no || "", r.invoice_date ? fmtDate(r.invoice_date) : "", r.invoice_value || "", r.vehicle_no || "", r.vehicle_type || "", r.freight_amount || 0, dDate(r) ? fmtDate(dDate(r)) : ""].map(cell).join(","));
      });
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `delivery-report-${from}_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader
        title="Delivery Report"
        subtitle="Deliveries made by each staff member in a chosen period."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportCsv} disabled={!chosen.length} data-testid="delivery-excel-btn">
              <FileDown className="mr-2 h-4 w-4" /> Excel{selected.size ? ` (${selected.size})` : ""}
            </Button>
            <Button variant="outline" onClick={() => printDeliveryReport(groups, from, to)} disabled={!chosen.length} data-testid="print-delivery-report-btn">
              <Printer className="mr-2 h-4 w-4" /> Print{selected.size ? ` (${selected.size})` : ""}
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card px-4 py-3">
        <div className="grid gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              className="h-9 w-[190px] pl-8 pr-7 text-sm"
              placeholder="Customer / code / LR"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="delivery-search"
            />
            {q && (
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setQ("")} data-testid="delivery-search-clear">
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="grid gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">From</Label>
          <Input type="date" className="h-9 w-[160px] text-sm" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="delivery-from-date" />
        </div>
        <div className="grid gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">To</Label>
          <Input type="date" className="h-9 w-[160px] text-sm" value={to} onChange={(e) => setTo(e.target.value)} data-testid="delivery-to-date" />
        </div>
        <div className="grid gap-1">
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Staff</Label>
          <Select value={staff} onValueChange={setStaff}>
            <SelectTrigger className="h-9 w-[200px] text-sm" data-testid="delivery-staff-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All staff</SelectItem>
              {staffNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="delivery-branch-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Branches</SelectItem>
              {branchNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <span className="ml-auto flex items-center gap-3">
          <Button className="h-9" onClick={load} data-testid="delivery-submit-btn">
            <Search className="mr-2 h-4 w-4" /> Submit
          </Button>
          <span className="text-xs text-muted-foreground" data-testid="delivery-count">{filtered.length} delivery(ies)</span>
        </span>
      </div>

      {rows === null && <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>}

      {rows !== null && (() => {
        const needle = q.trim().toLowerCase();
        const allPending = (rows || []).filter((r) => {
          if ((r.delivered_by || /delivered/i.test(r.status || "")) && dDate(r)) return false;
          if (/cancelled/i.test(r.status || "")) return false;
          if (needle) {
            const hay = [r.customer_name, custCode(r), cnOf(r), r.origin, r.destination, r.consignee, r.vehicle_no];
            if (!hay.some((x) => String(x || "").toLowerCase().includes(needle))) return false;
          }
          return true;
        });
        const byBranch = {};
        allPending.forEach((r) => {
          const b = r.office || r.office_name || "—";
          if (!byBranch[b]) byBranch[b] = { count: 0, maxAge: 0 };
          byBranch[b].count++;
          byBranch[b].maxAge = Math.max(byBranch[b].maxAge, ageDays(r) ?? 0);
        });
        const names = Object.keys(byBranch).sort();
        if (names.length < 2) return null;
        return (
          <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="pending-branch-pills">
            <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">Pending by branch:</span>
            {names.map((b) => {
              const s = byBranch[b];
              const cls = s.maxAge > 15 ? "bg-red-100 text-red-700" : s.maxAge > 7 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";
              const active = branch === b;
              return (
                <button
                  type="button"
                  key={b}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-transform hover:scale-105 ${cls} ${active ? "ring-2 ring-slate-700 ring-offset-1" : ""}`}
                  title={active ? "Tap again to show all branches" : `Show only ${b} — oldest pending ${s.maxAge} day(s)`}
                  onClick={() => setBranch(active ? "All" : b)}
                  data-testid={`pending-pill-${b.toLowerCase()}`}
                >
                  {b}: {s.count} pending
                </button>
              );
            })}
          </div>
        );
      })()}
      {rows !== null && groups.length === 0 && pending.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-slate-50/50 py-16 text-center text-sm text-muted-foreground" data-testid="delivery-empty">
          No deliveries in this period.
        </div>
      )}

      {rows !== null && pending.length > 0 && (
        <div className="mb-6" data-testid="pending-deliveries-section">
          <h2 className="mb-2 flex flex-wrap items-center gap-3 font-display text-base font-bold text-amber-700 md:text-lg">
            <span>
              Pending Deliveries <span className="text-sm font-normal text-muted-foreground">— {pending.length} consignment(s) not yet delivered, oldest first</span>
            </span>
            <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700" data-testid="pending-freight-total">
              Freight stuck: {inr(pending.reduce((s, r) => s + (Number(r.freight_amount) || 0), 0))}
            </span>
            <span className="rounded-full border border-sky-300 bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700" data-testid="pending-invoice-total">
              Invoice value stuck: {inr(pending.reduce((s, r) => s + (Number(r.invoice_value) || 0), 0))}
            </span>
            <UiChaseButton pending={pending} ageDays={ageDays} custCode={custCode} />
          </h2>
          <PagedList items={pending} noun="pending" testPrefix="pending-deliveries">
            {(pageRows, offset) => (
          <TableShell>
            <Table>
              <TableHeader>
                <TableRow className="bg-amber-50 hover:bg-amber-50">
                  {["Sl", "Branch", "LR No.", "LR Date", "From", "Destination", "Consignor Name", "Customer Code", "Consignee Name", "Basis", "PKG", "Invoice No.", "Invoice Date", "Invoice Value", "Vehicle No", "Vehicle Type", "Freight Amount", "POD", "Delivered On", "Actions"].map((h) => (
                    <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-amber-700">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((r, i) => (
                  <TableRow key={r.id} data-testid="pending-delivery-row">
                    <TableCell className="text-sm">{offset + i + 1}</TableCell>
                    <TableCell className="text-sm"><BranchCell value={r.office || "Chennai"} /></TableCell>
                    <TableCell className="text-sm font-medium">{cnOf(r) || "—"}</TableCell>
                    <TableCell className="text-sm">{r.lr_date || r.dispatch_date ? fmtDate(r.lr_date || r.dispatch_date) : "—"}</TableCell>
                    <TableCell className="text-sm">{r.origin || "—"}</TableCell>
                    <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                    <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                    <TableCell className="text-sm font-medium">{custCode(r) || "—"}</TableCell>
                    <TableCell className="text-sm">{r.consignee || "—"}</TableCell>
                    <TableCell className="text-sm" data-testid="pending-basis-cell">{r.basis || "—"}</TableCell>
                    <TableCell className="text-sm">{r.packages || "—"}</TableCell>
                    <TableCell className="text-sm" data-testid="pending-invoice-no">{r.invoice_no || "—"}</TableCell>
                    <TableCell className="text-sm">{r.invoice_date ? fmtDate(r.invoice_date) : "—"}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{Number(r.invoice_value) ? inr(r.invoice_value) : "—"}</TableCell>
                    <TableCell className="text-sm">{r.vehicle_no || "—"}</TableCell>
                    <TableCell className="text-sm">{r.vehicle_type || "—"}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums" data-testid="pending-freight-cell">{Number(r.freight_amount) ? inr(r.freight_amount) : "—"}</TableCell>
                    <TableCell className="text-sm">{podCell(r)}</TableCell>
                    <TableCell className="text-sm font-semibold text-amber-600">
                      <span className="inline-flex items-center gap-1.5">
                        {r.status || "Pending"}
                        {ageDays(r) !== null && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${ageCls(ageDays(r))}`} title={`Waiting ${ageDays(r)} day(s) since booking`} data-testid="pending-age-badge">
                            {ageDays(r)}d
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" className="h-7 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={() => { setMarkRow(r); setMarkDate(today()); }} data-testid="mark-delivered-btn">
                          Mark Delivered
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit" onClick={() => openEdit(r)} data-testid="edit-delivery-btn">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete consignment" onClick={() => deleteRow(r)} data-testid="delete-delivery-btn">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>
            )}
          </PagedList>
        </div>
      )}

      {groups.map(([name, list], gi) => (
        <div key={name} className="mb-6" data-testid="delivery-group">
          <h2 className="mb-2 font-display text-base font-bold md:text-lg">
            {name} <span className="text-sm font-normal text-muted-foreground">— {list.length} delivery(ies)</span>
          </h2>
          <PagedList items={list} noun="deliveries" testPrefix={`delivery-group-${gi}`}>
            {(pageRows, offset) => (
          <TableShell>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="w-10">
                    <Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleAll} data-testid="delivery-select-all" />
                  </TableHead>
                  {["Sl", "Branch", "LR No.", "LR Date", "From", "Destination", "Consignor Name", "Customer Code", "Consignee Name", "Basis", "PKG", "Invoice No.", "Invoice Date", "Invoice Value", "Vehicle No", "Vehicle Type", "Freight Amount", "POD", "Delivered On", "Actions"].map((h) => (
                    <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((r, i) => (
                  <TableRow key={r.id} data-testid="delivery-row">
                    <TableCell>
                      <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid="delivery-row-select" />
                    </TableCell>
                    <TableCell className="text-sm">{offset + i + 1}</TableCell>
                    <TableCell className="text-sm"><BranchCell value={r.office || "Chennai"} /></TableCell>
                    <TableCell className="text-sm font-medium">{cnOf(r) || "—"}</TableCell>
                    <TableCell className="text-sm">{r.lr_date || r.dispatch_date ? fmtDate(r.lr_date || r.dispatch_date) : "—"}</TableCell>
                    <TableCell className="text-sm">{r.origin || "—"}</TableCell>
                    <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                    <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                    <TableCell className="text-sm font-medium" data-testid="delivery-cust-code">{custCode(r) || "—"}</TableCell>
                    <TableCell className="text-sm">{r.consignee || "—"}</TableCell>
                    <TableCell className="text-sm" data-testid="delivery-basis-cell">{r.basis || "—"}</TableCell>
                    <TableCell className="text-sm">{r.packages || "—"}</TableCell>
                    <TableCell className="text-sm" data-testid="delivery-invoice-no">{r.invoice_no || "—"}</TableCell>
                    <TableCell className="text-sm">{r.invoice_date ? fmtDate(r.invoice_date) : "—"}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{Number(r.invoice_value) ? inr(r.invoice_value) : "—"}</TableCell>
                    <TableCell className="text-sm" data-testid="delivery-vehicle-no">{r.vehicle_no || "—"}</TableCell>
                    <TableCell className="text-sm" data-testid="delivery-vehicle-type">{r.vehicle_type || "—"}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums" data-testid="delivery-freight-cell">{Number(r.freight_amount) ? inr(r.freight_amount) : "—"}</TableCell>
                    <TableCell className="text-sm" data-testid="delivery-pod-cell">{podCell(r)}</TableCell>
                    <TableCell className="text-sm" data-testid="delivery-delivered-on">{fmtDate(dDate(r))}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {r.delivery_otp_confirmed ? (
                          <span
                            className="inline-flex items-center gap-1 self-center rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700"
                            title={`Delivery confirmed by consignee OTP on ${fmtDate(String(r.delivery_otp_confirmed_at || "").slice(0, 10))}`}
                            data-testid="delivery-otp-confirmed-badge"
                          >
                            <ShieldCheck className="h-3 w-3" /> OTP ✓
                          </span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-sky-600"
                            title="Confirm delivery with consignee OTP"
                            onClick={() => { setOtpRow(r); setOtpMobile(r.delivery_otp_mobile || ""); setOtpCode(""); setOtpSent(null); }}
                            data-testid="delivery-otp-btn"
                          >
                            <ShieldCheck className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" title="Send delivered confirmation on WhatsApp" onClick={() => whatsappDelivered(r)} data-testid="delivery-whatsapp-btn">
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit delivery" onClick={() => openEdit(r)} data-testid="edit-delivery-btn">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete consignment" onClick={() => deleteRow(r)} data-testid="delete-delivery-btn">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>
            )}
          </PagedList>
        </div>
      ))}

      <Dialog open={!!markRow} onOpenChange={(o) => !o && setMarkRow(null)}>
        <DialogContent className="sm:max-w-[380px]" data-testid="mark-delivered-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Mark Delivered — LR {markRow ? cnOf(markRow) : ""}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label>Delivered On</Label>
            <Input type="date" value={markDate} onChange={(e) => setMarkDate(e.target.value)} data-testid="mark-delivered-date" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkRow(null)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={markDelivered} data-testid="mark-delivered-save-btn">Mark Delivered</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!otpRow} onOpenChange={(o) => !o && setOtpRow(null)}>
        <DialogContent className="sm:max-w-[420px]" data-testid="delivery-otp-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Delivery OTP Confirm — LR {otpRow ? cnOf(otpRow) : ""}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Consignee Mobile</Label>
              <div className="flex gap-2">
                <Input value={otpMobile} onChange={(e) => setOtpMobile(e.target.value)} placeholder="10-digit mobile" data-testid="delivery-otp-mobile" />
                <Button
                  variant="outline"
                  disabled={otpBusy}
                  onClick={async () => {
                    setOtpBusy(true);
                    try {
                      const r = await api.post(`/shipments/${otpRow.id}/delivery-otp/send`, { mobile: otpMobile });
                      setOtpSent(r.data);
                      toast.success(r.data.sms_sent ? `OTP sent by SMS to ${r.data.mobile_masked}` : "OTP generated — share it with the consignee on WhatsApp");
                    } catch (e) {
                      toast.error(formatApiError(e.response?.data?.detail));
                    } finally {
                      setOtpBusy(false);
                    }
                  }}
                  data-testid="delivery-otp-send-btn"
                >
                  {otpSent ? "Resend OTP" : "Send OTP"}
                </Button>
              </div>
            </div>
            {otpSent && !otpSent.sms_sent && otpSent.code && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="delivery-otp-fallback">
                SMS unavailable — code: <b className="tracking-widest">{otpSent.code}</b>{" "}
                <button
                  type="button"
                  className="ml-1 font-bold text-emerald-700 underline underline-offset-2"
                  data-testid="delivery-otp-wa-btn"
                  onClick={() => {
                    const digits = String(otpMobile).replace(/\D/g, "").slice(-10);
                    const msg = `*NTC Delivery Confirmation*\nLR ${cnOf(otpRow)} — your one-time code to confirm receipt is *${otpSent.code}*.\nPlease tell this code to the delivery person.\n— Nisha Transport Corporation`;
                    window.open(`https://wa.me/${digits.length === 10 ? "91" + digits : ""}?text=${encodeURIComponent(msg)}`, "_blank");
                  }}
                >
                  Send on WhatsApp
                </button>
              </div>
            )}
            {otpSent && (
              <div className="grid gap-1.5">
                <Label>Code from Consignee</Label>
                <Input value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="Enter OTP" maxLength={8} data-testid="delivery-otp-code" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOtpRow(null)}>Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={!otpSent || !otpCode.trim() || otpBusy}
              onClick={async () => {
                setOtpBusy(true);
                try {
                  const r = await api.post(`/shipments/${otpRow.id}/delivery-otp/verify`, { code: otpCode.trim() });
                  setRows((prev) => prev.map((x) => (x.id === otpRow.id ? { ...x, delivery_otp_confirmed: true, delivery_otp_confirmed_at: r.data.confirmed_at } : x)));
                  toast.success(`LR ${cnOf(otpRow)} — delivery confirmed by consignee OTP`);
                  setOtpRow(null);
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail));
                } finally {
                  setOtpBusy(false);
                }
              }}
              data-testid="delivery-otp-verify-btn"
            >
              <ShieldCheck className="mr-1.5 h-4 w-4" /> Confirm Delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="sm:max-w-[420px]" data-testid="delivery-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Delivery — LR {editRow ? cnOf(editRow) : ""}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-1.5">
              <Label>Delivered On</Label>
              <Input type="date" value={editForm.delivery_date} onChange={(e) => setEditForm({ ...editForm, delivery_date: e.target.value })} data-testid="delivery-edit-date" />
            </div>
            <div className="grid gap-1.5">
              <Label>Vehicle No</Label>
              <Input value={editForm.vehicle_no} onChange={(e) => setEditForm({ ...editForm, vehicle_no: e.target.value })} placeholder="e.g. TN01AB1234" data-testid="delivery-edit-vehicle-no" />
            </div>
            <div className="grid gap-1.5">
              <Label>Vehicle Type</Label>
              <Input value={editForm.vehicle_type} onChange={(e) => setEditForm({ ...editForm, vehicle_type: e.target.value })} placeholder="e.g. 32 ft Container" data-testid="delivery-edit-vehicle-type" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="delivery-edit-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
